const PRICE_RANGE_MIN = 0

window.theme = window.theme || {}

window.theme.constructorApiHelpers = {
  priceRangeMin: PRICE_RANGE_MIN,

  flattenFacetOptions (options = []) {
    return options.reduce((flattenedOptions, option) => {
      flattenedOptions.push(option)

      if (Array.isArray(option.options) && option.options.length > 0) {
        flattenedOptions.push(...this.flattenFacetOptions(option.options))
      }

      return flattenedOptions
    }, [])
  },

  isFacetSelected (activeFacets = [], field, value) {
    return activeFacets.some((facet) => {
      if (facet.field !== field) return false

      return Array.isArray(facet.value)
        ? facet.value.includes(value)
        : facet.value === value
    })
  },

  parseRangeFacetValue (value) {
    const rangeValue = Array.isArray(value)
      ? value.length === 1
        ? value[0]
        : null
      : value

    if (typeof rangeValue !== 'string') return null

    const rangeParts = rangeValue.includes(':')
      ? rangeValue.split(':')
      : rangeValue
        .match(/^(-?inf|-?\d+(?:\.\d+)?)\s*-\s*(-?inf|-?\d+(?:\.\d+)?)$/i)
        ?.slice(1)

    if (!rangeParts || rangeParts.length !== 2) return null

    const parseBound = (bound) => {
      const normalizedBound = String(bound).trim().toLowerCase()

      if (normalizedBound === '-inf' || normalizedBound === 'inf') {
        return null
      }

      const parsedBound = Number.parseFloat(normalizedBound)

      return Number.isFinite(parsedBound) ? parsedBound : null
    }

    const min = parseBound(rangeParts[0])
    const max = parseBound(rangeParts[1])

    if (min == null && max == null) return null

    if (min == null || max == null) {
      return { min, max }
    }

    return {
      min: Math.min(min, max),
      max: Math.max(min, max),
    }
  },

  normalizeRangeMinValue (value, fallback = null) {
    const parsedValue = Number.parseFloat(value)

    return Number.isFinite(parsedValue)
      ? Math.max(this.priceRangeMin, Math.floor(parsedValue))
      : fallback
  },

  normalizeRangeMaxValue (value, fallback = null) {
    const parsedValue = Number.parseFloat(value)

    return Number.isFinite(parsedValue)
      ? Math.max(this.priceRangeMin, Math.ceil(parsedValue))
      : fallback
  },

  buildRangeFacetValue ({ min, max } = {}) {
    const normalizedMin = this.normalizeRangeMinValue(min)
    const normalizedMax = this.normalizeRangeMaxValue(max)

    return `${normalizedMin != null ? normalizedMin : '-inf'}-${normalizedMax != null ? normalizedMax : 'inf'}`
  },

  getSelectedRangeFacet (activeFacets = [], field) {
    const activeFacet = activeFacets.find((facet) => facet.field === field)
    if (!activeFacet) return null

    return this.parseRangeFacetValue(activeFacet.value)
  },

  mapFacet (facet, activeFacets = []) {
    if (facet?.type === 'range') {
      const min = Number.parseFloat(facet.min)
      const max = Number.parseFloat(facet.max)

      if (!Number.isFinite(min) || !Number.isFinite(max)) return null

      const rangeMin = this.priceRangeMin
      const rangeMax = this.normalizeRangeMaxValue(
        Math.max(min, max),
        rangeMin,
      )
      const selectedRange = this.getSelectedRangeFacet(
        activeFacets,
        facet.name,
      )
      const selectedMin = this.normalizeRangeMinValue(
        selectedRange?.min,
        rangeMin,
      )
      const selectedMax = this.normalizeRangeMaxValue(
        selectedRange?.max,
        rangeMax,
      )

      return {
        field: facet.name,
        name: facet.display_name || facet.name,
        type: 'range',
        min: rangeMin,
        max: rangeMax,
        selected: !!selectedRange,
        selectedMin,
        selectedMax,
      }
    }

    const options = Array.isArray(facet?.options)
      ? this.flattenFacetOptions(facet.options)
      : []

    if (options.length === 0) return null

    const data = options
      .filter((option) => option?.value)
      .map((option) => ({
        count: option.count,
        selected: this.isFacetSelected(activeFacets, facet.name, option.value),
        value: option.value,
      }))

    if (data.length === 0) return null

    return {
      field: facet.name,
      name: facet.display_name || facet.name,
      data,
    }
  },

  mapProduct (result = {}) {
    const productUrl = result?.data?.url || result?.url || ''
    const itemData = result?.data || {}

    if (!productUrl) return null

    return {
      itemType: 'product',
      itemName: itemData.name || result?.value || '',
      itemPrice: this.getItemPrice(itemData),
      productId: result?.data?.id || result?.value || '',
      variationId: itemData.variation_id || itemData.variationId || '',
      url: productUrl,
      result_id: result?.result_id || null,
      rawData: result,
    }
  },

  getFirstDefinedValue (source = {}, keys = [], fallback = '') {
    const values = Array.isArray(keys) ? keys : [keys]

    for (const key of values) {
      const value = source?.[key]

      if (value !== undefined && value !== null && value !== '') {
        return value
      }
    }

    return fallback
  },

  normalizePromotionPosition (value) {
    const position = Number.parseInt(value, 10)

    if (!Number.isFinite(position) || position <= 0) {
      return null
    }

    return position
  },

  mapPromotion (content = {}) {
    const data = content?.data || {}
    const resultsPositionOnPage = this.normalizePromotionPosition(
      this.getFirstDefinedValue(data, [
        'results_position',
        'resultsPosition',
        'position',
      ]),
    )
    const assetUrl = this.getFirstDefinedValue(data, ['assetUrl'])
    const mobileAssetUrl = this.getFirstDefinedValue(data, ['mobileAssetUrl'])
    const resolvedAssetUrl = assetUrl || mobileAssetUrl
    const resolvedMobileAssetUrl = mobileAssetUrl || assetUrl
    const header = this.getFirstDefinedValue(data, ['header'])
    const body = this.getFirstDefinedValue(data, ['body'])
    const ctaText = this.getFirstDefinedValue(data, ['ctaText'])
    const ctaLink = this.getFirstDefinedValue(data, ['ctaLink'])
    const altText = this.getFirstDefinedValue(data, ['altText'])

    if (!resultsPositionOnPage) {
      return null
    }

    if (
      !resolvedAssetUrl &&
      !resolvedMobileAssetUrl &&
      !header &&
      !body &&
      !(ctaText && ctaLink)
    ) {
      return null
    }

    return {
      itemType: 'promotion',
      altText,
      assetUrl: resolvedAssetUrl,
      body,
      ctaLink,
      ctaText,
      header,
      mobileAssetUrl: resolvedMobileAssetUrl || resolvedAssetUrl,
      rawData: content,
      resultsPositionOnPage,
    }
  },

  mapPromotions (refinedContent = []) {
    if (!Array.isArray(refinedContent)) {
      return []
    }

    return refinedContent
      .map((content) => this.mapPromotion(content))
      .filter(Boolean)
      .sort((firstPromotion, secondPromotion) => {
        return (
          firstPromotion.resultsPositionOnPage -
          secondPromotion.resultsPositionOnPage
        )
      })
  },

  buildRenderItems ({ products = [], promotions = [], slotCount = 0 } = {}) {
    const normalizedProducts = products
      .filter(Boolean)
      .map((product, index) => ({
        ...product,
        itemType: product.itemType || 'product',
        resultPositionOnPage: product.resultPositionOnPage || index + 1,
      }))
    const visibleSlotCount = Math.max(
      Number(slotCount) || 0,
      normalizedProducts.length,
    )
    const normalizedPromotions = promotions
      .filter(Boolean)
      .map((promotion) => ({
        ...promotion,
        itemType: 'promotion',
      }))

    if (visibleSlotCount === 0) {
      return []
    }

    if (normalizedPromotions.length === 0) {
      return normalizedProducts
    }

    const renderItems = []
    let productIndex = 0
    let promotionIndex = 0

    for (let slotIndex = 1; slotIndex <= visibleSlotCount; slotIndex += 1) {
      const nextPromotion = normalizedPromotions[promotionIndex]

      if (nextPromotion && nextPromotion.resultsPositionOnPage <= slotIndex) {
        renderItems.push(nextPromotion)
        promotionIndex += 1
        continue
      }

      const nextProduct = normalizedProducts[productIndex]

      if (!nextProduct) {
        break
      }

      renderItems.push(nextProduct)
      productIndex += 1
    }

    return renderItems
  },

  createProductCardItem ({
    itemClassNames = ['prd-List_Item'],
    itemDataset = {},
    product = {},
    productTemplate,
  } = {}) {
    if (!productTemplate) {
      return null
    }

    const clone = document.importNode(productTemplate.content, true)
    const productCard = clone.querySelector('product-card')

    if (!productCard) {
      return null
    }

    productCard.setAttribute('data-prop-data', JSON.stringify(product))

    const cardItem = document.createElement('li')

    itemClassNames.forEach((className) => {
      if (!className) {
        return
      }

      cardItem.classList.add(className)
    })

    Object.entries(itemDataset).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return
      }

      cardItem.dataset[key] = String(value)
    })

    cardItem.appendChild(clone)

    return {
      cardItem,
      productCard,
    }
  },

  createPromotionCardItem ({
    itemClassNames = ['prd-List_Item', 'cnstrc-PromoSlot'],
    itemDataset = {},
    promotion = {},
    promotionTemplate,
  } = {}) {
    if (!promotionTemplate) {
      return null
    }

    const clone = document.importNode(promotionTemplate.content, true)
    const promotionCard = clone.querySelector('[data-promotion-card]')

    if (!promotionCard) {
      return null
    }

    this.populatePromotionCard(clone, promotion)

    const cardItem = document.createElement('li')

    itemClassNames.forEach((className) => {
      if (!className) {
        return
      }

      cardItem.classList.add(className)
    })

    Object.entries(itemDataset).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return
      }

      cardItem.dataset[key] = String(value)
    })

    cardItem.appendChild(clone)

    return {
      cardItem,
      promotionCard,
    }
  },

  setPromotionCardVisibility (cardItem, isVisible = true) {
    if (!cardItem) {
      return
    }

    if (isVisible) {
      cardItem.style.removeProperty('visibility')
      cardItem.style.removeProperty('pointer-events')
      cardItem.removeAttribute('aria-hidden')
      return
    }

    cardItem.style.visibility = 'hidden'
    cardItem.style.pointerEvents = 'none'
    cardItem.setAttribute('aria-hidden', 'true')
  },

  waitForProductCards (productCards = []) {
    return Promise.allSettled(
      productCards.map((productCard) => {
        if (typeof productCard?.getRenderPromise !== 'function') {
          return Promise.resolve()
        }

        return productCard.getRenderPromise()
      }),
    )
  },

  populatePromotionCard (fragment, promotion = {}) {
    const promotionCard = fragment.querySelector('[data-promotion-card]')
    const mediaContainer = fragment.querySelector(
      '[data-promotion-card-el="media"]',
    )
    const mobileSource = fragment.querySelector(
      '[data-promotion-card-el="mobile-source"]',
    )
    const image = fragment.querySelector(
      '[data-promotion-card-el="image-tag"]',
    )
    const contentContainer = fragment.querySelector(
      '[data-promotion-card-el="content"]',
    )
    const header = fragment.querySelector('[data-promotion-card-el="header"]')
    const body = fragment.querySelector('[data-promotion-card-el="body"]')
    const cta = fragment.querySelector('[data-promotion-card-el="cta"]')
    const fauxLink = fragment.querySelector(
      '[data-promotion-card-el="faux-link"]',
    )
    const screenReaderLabel = fragment.querySelector(
      '[data-promotion-card-el="screen-reader-label"]',
    )
    const accessibleLabel =
      promotion.header || promotion.ctaText || promotion.altText || 'Promotion'
    const hasMedia = this.setPromotionImage({
      altText: promotion.altText || accessibleLabel,
      imageEl: image,
      mobileSourceEl: mobileSource,
      mobileUrl: promotion.mobileAssetUrl,
      url: promotion.assetUrl,
    })
    const hasContent = Boolean(
      promotion.header ||
        promotion.body ||
        (promotion.ctaText && promotion.ctaLink),
    )

    if (promotionCard) {
      promotionCard.setAttribute('data-theme-gtm-item-name', accessibleLabel)
    }

    if (mediaContainer) {
      mediaContainer.hidden = !hasMedia
    }

    if (contentContainer) {
      contentContainer.hidden = !hasContent
    }

    if (header) {
      header.hidden = !promotion.header
      header.textContent = promotion.header || ''
    }

    if (body) {
      body.hidden = !promotion.body
      body.textContent = promotion.body || ''
    }

    const safeCtaLink = (() => {
      if (!promotion.ctaLink) return ''
      try {
        const parsedUrl = new URL(promotion.ctaLink, window.location.origin)
        return ['http:', 'https:'].includes(parsedUrl.protocol)
          ? parsedUrl.href
          : ''
      } catch {
        return ''
      }
    })()

    if (cta) {
      const hasCta = Boolean(promotion.ctaText && safeCtaLink)

      cta.hidden = !hasCta

      if (hasCta) {
        cta.href = safeCtaLink
        cta.textContent = promotion.ctaText
      } else {
        cta.removeAttribute('href')
        cta.textContent = ''
      }
    }

    if (fauxLink) {
      fauxLink.hidden = !safeCtaLink

      if (safeCtaLink) {
        fauxLink.href = safeCtaLink
      } else {
        fauxLink.removeAttribute('href')
      }
    }

    if (screenReaderLabel) {
      screenReaderLabel.textContent = accessibleLabel
    }
  },

  setPromotionImage ({
    altText = '',
    imageEl,
    mobileSourceEl,
    mobileUrl = '',
    url = '',
  } = {}) {
    if (!imageEl || !url) {
      if (mobileSourceEl) {
        mobileSourceEl.removeAttribute('srcset')
      }

      if (imageEl) {
        imageEl.alt = ''
        imageEl.removeAttribute('src')
      }

      return false
    }

    imageEl.alt = altText
    imageEl.src = url

    if (mobileSourceEl) {
      if (mobileUrl && mobileUrl !== url) {
        mobileSourceEl.srcset = mobileUrl
      } else {
        mobileSourceEl.removeAttribute('srcset')
      }
    }

    return true
  },

  getItemPrice (itemData = {}) {
    const prices = [
      Number.parseFloat(itemData.price),
      Number.parseFloat(itemData.list_price || itemData.listPrice),
    ].filter((price) => Number.isFinite(price))

    if (prices.length === 0) {
      return ''
    }

    return String(Math.min(...prices))
  },

  buildFilterParameters (activeFacets = []) {
    const filters = {}
    const filterMatchTypes = {}

    activeFacets.forEach((facet) => {
      const values = Array.isArray(facet.value)
        ? facet.value.filter(Boolean)
        : [facet.value].filter(Boolean)

      if (values.length === 0) return

      const existingValues = filters[facet.field]
      const normalizedExistingValues = Array.isArray(existingValues)
        ? existingValues
        : existingValues != null
          ? [existingValues]
          : []
      const nextValues = Array.from(
        new Set([...normalizedExistingValues, ...values]),
      )

      filters[facet.field] =
        nextValues.length === 1 ? nextValues[0] : nextValues

      if (nextValues.length > 1) {
        filterMatchTypes[facet.field] = 'any'
      }
    })

    return {
      filters: Object.keys(filters).length > 0 ? filters : undefined,
      filterMatchTypes:
        Object.keys(filterMatchTypes).length > 0 ? filterMatchTypes : undefined,
    }
  },
}
